/**
 * Image Model Definition Hook
 *
 * Specialized hook for fetching image model definitions and their parameter schemas.
 * Used by ImageGeneratorPage to render data-driven parameter controls.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  ModelDefinition,
  ModelDefinitionListItem,
  ModelProvider,
  ParameterDefinition,
} from '../api';
import { queryKeys } from '../query-keys';

/**
 * Image model capabilities (extends base capabilities with image-specific fields)
 */
export interface ImageModelCapabilities {
  tier: string;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  isReasoningModel: boolean;
  // Image-specific capabilities
  supportedSizes?: string[];
  supportedQualities?: string[];
  supportedStyles?: string[];
  supportsPromptRevision?: boolean;
  maxPromptLength?: number;
}

/**
 * Options for the useImageModelDefinition hook
 */
export interface UseImageModelDefinitionOptions {
  /** Entity ID to fetch by ID */
  entityId?: string;
  /** Provider to lookup by provider and model name */
  provider?: ModelProvider;
  /** Model name to lookup by provider and model name */
  modelName?: string;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * Result returned by the useImageModelDefinition hook
 */
export interface UseImageModelDefinitionResult {
  /** The model definition (if loaded) */
  definition: ModelDefinition | null;
  /** The parameter schema from the definition */
  parameterSchema: ParameterDefinition[];
  /** Image-specific capabilities */
  capabilities: ImageModelCapabilities | null;
  /** Supported image sizes from capabilities */
  supportedSizes: string[];
  /** Supported quality levels from capabilities */
  supportedQualities: string[];
  /** Supported styles from capabilities */
  supportedStyles: string[];
  /** Whether the model supports prompt revision */
  supportsPromptRevision: boolean;
  /** Maximum prompt length in characters */
  maxPromptLength: number;
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refresh the definition */
  refresh: () => Promise<void>;
}

/**
 * Result returned by useImageModels hook
 */
export interface UseImageModelsResult {
  /** Available image models */
  models: ModelDefinitionListItem[];
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refresh the list */
  refresh: () => Promise<void>;
}

/**
 * React hook for fetching available image models.
 *
 * @example
 * ```tsx
 * function ModelSelector() {
 *   const { models, loading } = useImageModels();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Select>
 *       {models.map(model => (
 *         <SelectItem key={model.modelName} value={model.modelName}>
 *           {model.displayName}
 *         </SelectItem>
 *       ))}
 *     </Select>
 *   );
 * }
 * ```
 */
export function useImageModels(): UseImageModelsResult {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.models.imageModels(),
    queryFn: () => api.listModelDefinitions({ modelType: 'image' }),
  });

  return {
    models: data?.definitions ?? [],
    loading: isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    refresh: async () => {
      await refetch();
    },
  };
}

/**
 * React hook for fetching an Image Model Definition Entity by ID or provider/modelName lookup.
 *
 * Returns the full model definition including parameter schema and image-specific
 * capabilities, which can be used to render data-driven parameter controls.
 *
 * @example
 * ```tsx
 * function ImageSettings({ provider, modelName }) {
 *   const {
 *     parameterSchema,
 *     supportedSizes,
 *     supportedQualities,
 *     supportedStyles,
 *     loading,
 *   } = useImageModelDefinition({ provider, modelName });
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <EntityParameterPanel
 *       parameterSchema={parameterSchema}
 *       values={values}
 *       onChange={setValues}
 *     />
 *   );
 * }
 * ```
 */
export function useImageModelDefinition(
  options: UseImageModelDefinitionOptions = {},
): UseImageModelDefinitionResult {
  const { entityId, provider, modelName, autoFetch = true } = options;

  const hasEntityId = !!entityId;
  const hasLookup = !!provider && !!modelName;
  const hasIdentifiers = hasEntityId || hasLookup;

  const {
    data: definition,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: hasEntityId
      ? queryKeys.models.definition(entityId!)
      : hasLookup
        ? queryKeys.models.definitionByLookup(provider!, modelName!)
        : queryKeys.models.definition('__none__'),
    queryFn: async () => {
      let result: ModelDefinition;

      if (entityId) {
        result = await api.getModelDefinition(entityId);
      } else {
        result = await api.getModelDefinitionByLookup(provider!, modelName!);
      }

      // Verify it's an image model
      if (result.modelType !== 'image') {
        throw new Error(`Model ${result.modelName} is not an image model`);
      }

      return result;
    },
    enabled: autoFetch && hasIdentifiers,
  });

  // Extract image-specific capabilities
  const capabilities = useMemo(() => {
    if (!definition?.capabilities) return null;
    return definition.capabilities as unknown as ImageModelCapabilities;
  }, [definition]);

  return {
    definition: definition ?? null,
    parameterSchema: definition?.parameterSchema || [],
    capabilities,
    supportedSizes: capabilities?.supportedSizes || [],
    supportedQualities: capabilities?.supportedQualities || [],
    supportedStyles: capabilities?.supportedStyles || [],
    supportsPromptRevision: capabilities?.supportsPromptRevision || false,
    maxPromptLength: capabilities?.maxPromptLength || 4000,
    loading: isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    refresh: async () => {
      await refetch();
    },
  };
}

/**
 * Creates default parameter values from an image parameter schema
 */
export function createDefaultImageParameterValues(
  schema: ParameterDefinition[],
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const param of schema) {
    let defaultValue: unknown;

    if (param.defaultValue !== undefined) {
      defaultValue = param.defaultValue;
    } else if (param.type === 'number' && param.range) {
      defaultValue = param.range.default;
    } else if (param.type === 'enum' && param.options && param.options.length > 0) {
      defaultValue = param.options[0].value;
    } else if (param.type === 'boolean') {
      defaultValue = false;
    }

    if (defaultValue !== undefined) {
      defaults[param.name] = defaultValue;
    }
  }

  return defaults;
}
